import { CalendarDaysIcon } from '@heroicons/react/20/solid';
import { ClockIcon, LinkSlashIcon } from '@heroicons/react/24/outline';
import { PrimaryButtonClassname } from '@shared/components/buttons';
import { EVENTS, track } from '@shared/helpers/analytics';
import { CALENDLY_LINK_URL } from '@shared/helpers/calendly';
import { getUtm, utmProps } from '@shared/helpers/utm';
import { EmptyState } from './empty-state';

/** Why a trial link session is over. */
export type TrialClosedReason = 'ended' | 'invalid';

const COPY: Record<
  TrialClosedReason,
  { icon: React.ReactNode; title: string; description: string }
> = {
  // Its 24h are up. The store and its data are kept, and Dirk can reopen the
  // same link from Admin → Trials.
  ended: {
    icon: <ClockIcon className="size-8" />,
    title: 'This demo has ended',
    description:
      'Your store and its data are saved. Book a call with Dirk to pick up where you left off.',
  },
  // The link opens nothing: mistyped or cut short, or the store has since
  // become a real one (ORD-518) and is reached by signing in.
  invalid: {
    icon: <LinkSlashIcon className="size-8" />,
    title: "This demo link isn't working",
    description:
      "It may be incomplete, or this store may have moved to a full Orderbot account. Book a call with Dirk and he'll get you set up.",
  },
};

// What a trial link shows once its session is over (ORD-514): either way, the
// way forward is a call with Dirk.
export function TrialClosed({
  reason,
  onSignIn,
}: {
  reason: TrialClosedReason;
  onSignIn: () => void;
}) {
  const { icon, title, description } = COPY[reason];

  return (
    <main className="h-full bg-white">
      <EmptyState
        icon={icon}
        title={title}
        description={description}
        action={
          <a
            href={CALENDLY_LINK_URL}
            target="_blank"
            rel="noopener noreferrer"
            className={PrimaryButtonClassname}
            onClick={() => track(EVENTS.scheduleDemoClick, utmProps(getUtm()))}
          >
            <CalendarDaysIcon className="h-5 w-5" />
            Book a call with Dirk
          </a>
        }
        footnote={
          reason === 'invalid' ? (
            <>
              Already have an Orderbot account?{' '}
              <button type="button" onClick={onSignIn} className="text-primary-500 cursor-pointer">
                Sign in
              </button>
            </>
          ) : (
            <>
              Questions?{' '}
              <a href="mailto:hello@liquorstore.tech" className="text-primary-500">
                hello@liquorstore.tech
              </a>
            </>
          )
        }
      />
    </main>
  );
}
